import React, { useState, FC, CSSProperties, useEffect } from 'react';
import { dropdownStyle } from '../components/StyledSelect';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { useAuth } from "../contexts/AuthContext";
import { apiUrl } from "../utils/api";
import { useFilters } from '../contexts/FiltersContext';

// --- Type Definitions ---

interface PieChartDataPoint {
    name: string;
    value: number;
    color: string;
    [key: string]: string | number;
}

interface TableRowData {
    module: string;
    serialNumber: string;
    panel: string;
    processing: string;
    hours: string;
}

interface LegendProps {
    data: PieChartDataPoint[];
}

interface StationCardProps {
    title: string;
    data: PieChartDataPoint[];
    npv: string;
    pv: string;
    simple: string;
}

// --- Mock Data ---

interface StationListItem {
    date: string;
    company: string;
    location: string;
    station: string;
}

const stationsList: StationListItem[] = [
    { date: '12.03.2025 13:31', company: 'Есаульская', location: 'Завка на Кш 26-29', station: 'Станок 1' },
    { date: '12.03.2025 13:31', company: 'НШ - 2', location: '2 - Ц', station: 'Станок 4' },
    { date: '12.03.2025 13:31', company: 'НШ - 2', location: '2 - Ц', station: 'Станок 2' },
    { date: '12.03.2025 13:31', company: 'Есаульская', location: '2 - Ц', station: 'Станок 3' },
    { date: '12.03.2025 13:31', company: 'Есаульская', location: '2 - Ц', station: 'Станок 3' }
];

// --- API types for /npt ---
interface NptEntry { name: string; pct: number }
interface NptNode { npt_pct: number; pt_pct: number; idle_pct: number; diagram_entries: NptEntry[] }
interface NptResponse { code: number; message: string; diagram_data: Record<string, NptNode>; table_data?: unknown }
interface RigItem { id: string; name: string; status?: string; well_id?: string; contractor_id?: string; id_int?: string }
// Removed unused SummaryStatic type

const PIE_COLORS = ['#ff4444','#ff8800','#ffaa00','#ffdd00','#888888','#444444','#4472C4','#00ddff','#00ffaa','#88ff88','#00ff44'];

const tableData: TableRowData[] = [
    { module: 'Cell Data', serialNumber: 'Cell Data', panel: 'Cell Data', processing: 'Cell Data', hours: 'Cell Data' },
    { module: 'Cell Data', serialNumber: 'Cell Data', panel: 'Cell Data', processing: 'Cell Data', hours: 'Cell Data' },
    { module: 'Cell Data', serialNumber: 'Cell Data', panel: 'Cell Data', processing: 'Cell Data', hours: 'Cell Data' },
    { module: 'Cell Data', serialNumber: 'Cell Data', panel: 'Cell Data', processing: 'Cell Data', hours: 'Cell Data' },
    { module: 'Cell Data', serialNumber: 'Cell Data', panel: 'Cell Data', processing: 'Cell Data', hours: 'Cell Data' },
    { module: 'Cell Data', serialNumber: 'Cell Data', panel: 'Cell Data', processing: 'Cell Data', hours: 'Cell Data' },
    { module: 'Cell Data', serialNumber: 'Cell Data', panel: 'Cell Data', processing: 'Cell Data', hours: 'Cell Data' },
    { module: 'Cell Data', serialNumber: 'Cell Data', panel: 'Cell Data', processing: 'Cell Data', hours: 'Cell Data' }
];

// --- Components ---

interface AddStationModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const AddStationModal: FC<AddStationModalProps> = ({ isOpen, onClose }) => {
    if (!isOpen) return null;

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 500 }}>Добавить станок</h3>
                </div>
                <div style={styles.modalBody}>
                    <div style={styles.formGrid}>
                        <select style={{ ...dropdownStyle, width: '180px' }}>
                            <option value="">Буровая</option>
                        </select>
                        <select style={{ ...dropdownStyle, width: '180px' }}>
                            <option value="">Месторождение</option>
                        </select>
                        <select style={{ ...dropdownStyle, width: '180px' }}>
                            <option value="">Компания</option>
                        </select>
                    </div>
                    <div style={styles.modalTable}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ borderBottom: '1px solid #333' }}>
                                    <th style={styles.modalTableHeader}>Время</th>
                                    <th style={styles.modalTableHeader}>Компания</th>
                                    <th style={styles.modalTableHeader}>Месторождение</th>
                                    <th style={styles.modalTableHeader}>Буровая</th>
                                </tr>
                            </thead>
                            <tbody>
                                {stationsList.map((item, index) => (
                                    <tr key={index} style={{ borderBottom: '1px solid #333' }}>
                                        <td style={styles.modalTableCell}>{item.date}</td>
                                        <td style={styles.modalTableCell}>{item.company}</td>
                                        <td style={styles.modalTableCell}>{item.location}</td>
                                        <td style={styles.modalTableCell}>{item.station}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div style={styles.modalFooter}>
                        <button style={styles.cancelButton} onClick={onClose}>Отмена</button>
                        <button style={{...styles.configButton, minWidth: '80px'}}>Добавить</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const Legend: FC<LegendProps> = ({ data }) => (
    <div style={{ marginLeft: '20px', alignSelf: 'center' }}>
        {data.map((item, index) => (
            <div key={index} style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '8px',
                fontSize: '14px',
                color: '#fff'
            }}>
                <div style={{
                    width: '12px',
                    height: '12px',
                    backgroundColor: item.color,
                    borderRadius: '50%',
                    marginRight: '8px'
                }}></div>
                <span>{item.name}</span>
            </div>
        ))}
    </div>
);

const StationCard: FC<StationCardProps> = ({ title, data, npv, pv, simple }) => (
    <div style={{
        background: '#1F2534',
        borderRadius: '16px',
        padding: '20px',
        color: '#fff',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid #2E3749',
        boxShadow: '0 2px 16px #0000003D'
    }}>
        <h3 style={{ 
            margin: '0 0 20px 0', 
            fontSize: '18px',
            fontWeight: '500',
        }}>{title}</h3>
        <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <div style={{ width: '250px', height: '250px' }}>
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie
                            data={data}
                            cx="50%"
                            cy="50%"
                            innerRadius={80}
                            outerRadius={110}
                            paddingAngle={0}
                            dataKey="value"
                        >
                            {data.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} stroke={entry.color} />
                            ))}
                        </Pie>
                    </PieChart>
                </ResponsiveContainer>
            </div>
            <Legend data={data} />
        </div>
        <div style={{
            display: 'flex',
            justifyContent: 'space-around',
            marginTop: '20px',
            fontSize: '32px',
            fontWeight: 'bold'
        }}>
            <div>
                НПВ {npv}%
            </div>
            <div>
                ПВ {pv}%
            </div>
            <div>
                Простой {simple}%
            </div>
        </div>
    </div>
);

const DrillingAnalyticsPage: FC = () => {
    const { user } = useAuth();
    const token = user?.access_token;
    const { dateFrom, dateTo } = useFilters();
    const [activeTab, setActiveTab] = useState('telemetry');
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [cards, setCards] = useState<Array<{ title: string; data: PieChartDataPoint[]; npv: string; pv: string; simple: string }>>([]);
    const [rigs, setRigs] = useState<RigItem[]>([]);
    const [selectedStations, setSelectedStations] = useState<string[]>([]);

    const mapResponseToCards = (data: NptResponse, rigsOverride?: RigItem[]) => {
        const dd = data.diagram_data || {};
        const rigsArr = rigsOverride ?? rigs;
        const nextCards: Array<{ title: string; data: PieChartDataPoint[]; npv: string; pv: string; simple: string }>= [];
        Object.entries(dd).forEach(([key, node]) => {
            const pie = (node.diagram_entries || []).map((e, i) => ({ name: e.name, value: e.pct, color: PIE_COLORS[i % PIE_COLORS.length] }));
            const label = rigsArr.find(r => r.id === key)?.name || key;
            nextCards.push({ title: label, data: pie, npv: String(node.npt_pct ?? 0), pv: String(node.pt_pct ?? 0), simple: String(node.idle_pct ?? 0) });
        });
        setCards(nextCards);
    };

    const fetchNpt = async (rigsOverride?: RigItem[]) => {
        try {
            const params = new URLSearchParams();
            selectedStations.forEach(id => params.append('station_id', id));
            if (dateFrom) params.set('period_from', dateFrom.toISOString());
            if (dateTo) params.set('period_to', dateTo.toISOString());
            const url = `${apiUrl('/npt/data')}${params.toString() ? `?${params.toString()}` : ''}`;
            const res = await fetch(url, {
                method: 'GET',
                headers: { 'Accept': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
            });
            if (!res.ok) throw new Error(`NPT fetch failed: ${res.status}`);
            const data: NptResponse = await res.json();
            mapResponseToCards(data, rigsOverride);
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        if (!token) return;
        const init = async () => {
            try {
                // Fetch stations list for selection via /summary/static (role-safe)
                const res = await fetch(apiUrl('/summary/static'), {
                    method: 'GET',
                    headers: { 'Accept': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
                });
                let rigsFromApi: RigItem[] = [];
                if (res.ok) {
                    const data = await res.json();
                    rigsFromApi = Array.isArray(data.station_list) ? data.station_list.map((s: any) => ({
                        id: s.id,
                        name: s.name,
                    })) : [];
                    setRigs(rigsFromApi);
                }
                await fetchNpt(rigsFromApi);
            } catch (e) { console.error(e); }
        };
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const addStation = (id: string) => {
        if (!id) return;
        setSelectedStations(prev => prev.includes(id) ? prev : [...prev, id]);
    };
    const removeStation = (id: string) => {
        setSelectedStations(prev => prev.filter(x => x !== id));
    };

    const getTabStyle = (tab: string) => ({
        ...styles.tabButton,
        color: activeTab === tab ? '#0066FF' : '#fff',
        background: activeTab === tab ? '#1E1E1E' : 'transparent'
    });

    return (
        <div style={{ padding: '10px 8px 0', color: '#fff', marginTop: 20 }}>
            <div style={styles.headerWrapper}>
                <div style={styles.filters}>
                    <select onChange={(e) => { addStation(e.target.value); e.currentTarget.value=''; }} style={dropdownStyle as any} value="">
                        <option value="" disabled>Добавить станок...</option>
                        {rigs.filter(r => !selectedStations.includes(r.id)).map(r => (
                            <option key={r.id} value={r.id}>{r.name}</option>
                        ))}
                    </select>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {selectedStations.map(id => {
                            const name = rigs.find(r => r.id === id)?.name || id;
                            return (
                                <span key={id} style={styles.tag as any}>
                                    {name}
                                    <button onClick={()=>removeStation(id)} style={styles.tagRemove as any}>×</button>
                                </span>
                            );
                        })}
                    </div>
                </div>
                <div style={styles.headerButtons}>
                    {/* <button style={styles.addButton} onClick={() => setIsAddModalOpen(true)}>Добавить станок</button> */}
                    <button style={styles.configButton} onClick={() => fetchNpt()}>Построить график</button>
                </div>
            </div>
            <AddStationModal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} />

            <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' }}>
                {cards.length > 0 ? (
                    cards.slice(0, 4).map((c, i) => (
                        <StationCard key={i} title={c.title} data={c.data} npv={c.npv} pv={c.pv} simple={c.simple} />
                    ))
                ) : (
                    <StationCard title="Станок" data={[{name:'Данные', value:100, color:'#444'}]} npv="0" pv="0" simple="0" />
                )}
            </div>

            <div style={{ background: '#1F2534', borderRadius: '16px', padding: '20px', border: '1px solid #2E3749', boxShadow: '0 2px 16px #0000003D' }}>
                
                <div style={{ display: 'flex', marginBottom: '20px', background: '#1C1D1F' }}>
                    <button 
                        style={getTabStyle('telemetry')}
                        onClick={() => setActiveTab('telemetry')}
                    >
                        Телеметрия
                    </button>
                    <button 
                        style={getTabStyle('bzd')}
                        onClick={() => setActiveTab('bzd')}
                    >
                        ВЗД
                    </button>
                    <button 
                        style={getTabStyle('bit')}
                        onClick={() => setActiveTab('bit')}
                    >
                        Долото
                    </button>
                    <button 
                        style={getTabStyle('drills')}
                        onClick={() => setActiveTab('drills')}
                    >
                        Буровые станки
                    </button>
                    <button 
                        style={getTabStyle('add')}
                        onClick={() => setActiveTab('add')}
                    >
                        Добавить оборудование
                    </button>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr style={{ borderBottom: '1px solid #444' }}>
                            <th style={styles.tableHeader}>Модуль</th>
                            <th style={styles.tableHeader}>Сер. номер</th>
                            <th style={styles.tableHeader}>Панель</th>
                            <th style={styles.tableHeader}>Обработка</th>
                            <th style={styles.tableHeader}>Часы</th>
                        </tr>
                    </thead>
                    <tbody>
                        {tableData.map((row, index) => (
                            <tr key={index} style={{ borderBottom: '1px solid #333' }}>
                                <td style={styles.tableCell}>{row.module}</td>
                                <td style={styles.tableCell}>{row.serialNumber}</td>
                                <td style={styles.tableCell}>{row.panel}</td>
                                <td style={styles.tableCell}>{row.processing}</td>
                                <td style={styles.tableCell}>{row.hours}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                        </div>
            
        </div>
    );
};

const styles: { [key: string]: CSSProperties } = {
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
    },
    modalContent: {
        background: '#1F2534',
        borderRadius: '8px',
        width: '100%',
        maxWidth: '600px',
        overflow: 'hidden',
        border: '1px solid #2E3749',
        boxShadow: '0 2px 16px #0000003D'
    },
    modalHeader: {
        padding: '16px 20px',
        color: '#fff',
        borderBottom: '1px solid #333'
    },
    modalBody: {
        padding: '16px 20px'
    },
    formGrid: {
        display: 'flex',
        gap: '8px',
        marginBottom: '20px'
    },
    // Use shared dropdownStyle; width overrides applied inline where needed
    modalDropdown: dropdownStyle,
    modalTable: {
        margin: '20px -20px',
        maxHeight: '300px',
        overflow: 'auto'
    },
    modalTableHeader: {
        padding: '12px 20px',
        textAlign: 'left',
        fontSize: '12px',
        color: '#aaa',
        fontWeight: '500',
        background: '#1C1D1F'
    },
    modalTableCell: {
        padding: '12px 20px',
        fontSize: '14px'
    },
    modalFooter: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '8px'
    },
    cancelButton: {
        background: 'transparent',
        color: '#fff',
        border: 'none',
        borderRadius: '4px',
        padding: '6px 16px',
        fontSize: '14px',
        cursor: 'pointer',
        minWidth: '80px'
    },
    headerWrapper: {
        marginBottom: '16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: '#1F2534',
        padding: '12px 16px',
        borderRadius: 8,
        border: '1px solid #2E3749',
        boxShadow: '0 2px 16px #0000003D'
    },
    machineStatus: {
        display: 'flex',
        gap: '8px',
        alignItems: 'center'
    },
    machineStatusItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        background: '#1C1D1F',
        padding: '6px 12px',
        borderRadius: '4px',
        fontSize: '14px'
    },
    statusDot: {
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        background: '#4CAF50'
    },
    closeButton: {
        background: 'transparent',
        border: 'none',
        color: '#666',
        fontSize: '18px',
        cursor: 'pointer',
        padding: '0 4px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    headerButtons: {
        display: 'flex',
        gap: '8px'
    },
    addButton: {
        background: '#d9534f',
        color: '#fff',
        border: 'none',
        borderRadius: '4px',
        padding: '6px 16px',
        fontSize: '14px',
        cursor: 'pointer',
        height: '32px',
        minWidth: '120px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    configButton: {
        background: '#0066FF',
        color: '#fff',
        border: 'none',
        borderRadius: '4px',
        padding: '6px 16px',
        fontSize: '14px',
        cursor: 'pointer',
        height: '32px',
        minWidth: '120px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    activeTab: {
        padding: '8px 16px',
        border: 'none',
        background: '#333',
        color: '#fff',
        cursor: 'pointer',
        borderRadius: '10px',
        fontSize: '14px'
    },
    filters: {
        display: 'flex',
        gap: '12px',
        alignItems: 'center',
        flexWrap: 'wrap'
    },
    dropdown: dropdownStyle,
    dateInput: {
        background: '#333',
        color: '#fff',
        border: '1px solid #444',
        borderRadius: '8px',
        padding: '7px 12px'
    },
    // Date picker button styles unified with ViolationsPage
    dateField: { display: 'flex', flexDirection: 'column', gap: 4, marginTop: -20 },
    dateLabel: { fontSize: '12px', color: '#808080', paddingLeft: '2px' },
    dateButton: {
        background: '#2C2C2C',
        color: '#fff',
        border: '1px solid #2F3136',
        borderRadius: 4,
        padding: '4px 12px',
        width: '260px',
        height: '28px',
        fontSize: '14px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: 'pointer'
    },
    dateText: { fontSize: '14px', color: '#fff' },
    dateIcon: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
    // Inline variant for horizontal alignment with dropdown
    dateInlineGroup: { display: 'flex', alignItems: 'center', gap: 6, marginTop: -2 },
    dateInlineLabel: { fontSize: '12px', color: '#aaa' },
    dateInlineButton: {
        background: '#333',
        color: '#fff',
        border: '1px solid #444',
        borderRadius: '8px',
        padding: '7px 12px',
        minWidth: '180px',
        fontSize: '14px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: 'pointer'
    },
    tableHeader: {
        padding: '12px',
        textAlign: 'left',
        fontSize: '14px',
        color: '#aaa',
        fontWeight: '500'
    },
    tableCell: {
        padding: '12px',
        fontSize: '14px'
    },
    tag: {
        background: '#2C2C2C',
        color: '#fff',
        borderRadius: '12px',
        padding: '4px 8px',
        fontSize: '12px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
    },
    tagRemove: {
        background: 'none',
        border: 'none',
        color: '#aaa',
        cursor: 'pointer',
        fontSize: '14px',
        lineHeight: '1',
        padding: '0',
    },
    tabButton: {
        background: 'transparent',
        color: '#fff',
        border: 'none',
        padding: '12px 20px',
        fontSize: '14px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'color 0.2s',
        borderRadius: 0
    }
};

export default DrillingAnalyticsPage;
